import type { ReactNode } from "react";
import { IconBadge } from "@/components/waggle/PageShell";
import { cn } from "@/lib/utils";

type Tone = "brand" | "muted" | "warning";

const badgeTone: Record<Tone, { tone: "brand" | "muted"; className?: string }> = {
  brand: { tone: "brand" },
  muted: { tone: "muted" },
  warning: { tone: "brand", className: "bg-amber-100 text-amber-700 ring-amber-50" },
};

/**
 * The page-level "hero" slot: resume prompts, first-run nudges and empty
 * states all share this treatment — soft brand gradient, icon badge, title,
 * one-paragraph description, and the call to action underneath.
 */
export function HeroCard({
  icon,
  title,
  description,
  eyebrow,
  actions,
  tone = "brand",
  className,
  ...rest
}: {
  icon: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** Small pill above the title, e.g. "In progress · updated today". */
  eyebrow?: ReactNode;
  actions?: ReactNode;
  tone?: Tone;
  className?: string;
  "data-testid"?: string;
}) {
  const badge = badgeTone[tone];

  return (
    <div
      className={cn(
        "flex flex-col items-start gap-5 rounded-2xl border p-6 sm:flex-row",
        tone === "muted" ? "bg-linear-to-br from-muted/70 to-background" : "bg-linear-to-br from-brand-50 to-background",
        className,
      )}
      {...rest}
    >
      <IconBadge tone={badge.tone} className={cn("hidden sm:flex", badge.className)}>
        {icon}
      </IconBadge>
      <div>
        {eyebrow && (
          <span
            className={cn(
              "mb-2 inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
              tone === "warning" ? "border-amber-200 bg-amber-50 text-amber-800" : "border-brand-200 bg-brand-50 text-brand-700",
            )}
          >
            {eyebrow}
          </span>
        )}
        <h2 className="text-lg font-semibold">{title}</h2>
        {description && <p className="mt-1 max-w-lg text-sm text-muted-foreground">{description}</p>}
        {actions && <div className="mt-4 flex flex-wrap gap-3">{actions}</div>}
      </div>
    </div>
  );
}
